// How a coffee's front looks, and the photographic compositing values the wall
// uses to sit a bag into a plate. One source for BagFront.vue and ShelfWall.vue.
//
// A front is one of three things (src/data/bag-fronts.json):
//   cut      a transparent cut-out of the real bag   public/bags/cut/<slug>.webp
//   crop     the raw product crop of the real bag     public/bags/<slug>.webp (edge clipped)
//   sticker  the roaster only photographs a label, a tin or a lifestyle shot, so we
//            print that photo as a die-cut sticker and put it on a blank pouch,
//            the way small roasters really label stock bags.

import fronts from '../data/bag-fronts.json';
import { roasterColour } from '../store.js';

const BASE = import.meta.env.BASE_URL || '/';
const asset = (p) => BASE.replace(/\/$/, '') + p;

// Blank pouches (public/wall/pouch). Aspect is the image's w/h.
export const POUCHES = {
  white: { src: asset('/wall/pouch/white.webp'), aspect: 475 / 768 },
  kraft: { src: asset('/wall/pouch/kraft.webp'), aspect: 477 / 768 },
  black: { src: asset('/wall/pouch/black.webp'), aspect: 494 / 768 },
};

// Where the sticker sits on a pouch, in pouch fractions. The pouches' zip is at
// ~12% and the valve at ~22%, so the label goes just below the valve.
export const STICKER = {
  width: 0.66,          // of the pouch width
  maxHeight: 0.44,      // of the pouch height
  centreY: 0.55,        // label centre, of the pouch height
  aspect: [0.78, 1.45], // the photo is cropped (cover) into this w/h range
  border: 0.035,        // white die-cut margin, of the label width
  radius: 0.06,         // corner radius, of the label width
  tilt: 2.4,            // ±degrees, seeded per coffee
};

// How each roaster's sticker photo is cropped. The sources differ:
//   collusion   already a printed label card (header + photo): show it whole
//   almanac     the bag on a coloured ground behind giant letters: crop into the
//               bag's illustrated front panel, which is the label
//   brew-methods  the photo is a blank frosted pouch, so a photo of it on a pouch
//               would be a pouch on a pouch. They ship in plain frosted pouches
//               with a small printed label, so that is what we draw: `typed`.
export const STICKER_STYLE = {
  'almanac-coffee': { zoom: 2.35, fx: 0.5, fy: 0.58, aspect: 1.15 },
  'collusion-coffee': { zoom: 1.06, fx: 0.5, fy: 0.5 },
  'brew-methods': { typed: true, aspect: 1.1 },
};

// The raw crops have a hairline of background on their edges.
export const CROP_CLIP = 'inset(1.4% round 3px)';

// Photographic compositing on the plates (all four share one key light, from
// the upper right, so shadows fall down and to the left).
export const LOOK = {
  frontShadow: 'drop-shadow(-5px 5px 5px rgba(40, 26, 12, .30))',
  frontGrade: 'sepia(.06) saturate(.96)',       // pulls studio-white product shots toward the warm room
  stackBrightness: [0.86, 0.09],                // base, minus per bag further back
  stackGrade: 'saturate(.9) contrast(.96)',
  stackRise: [0.30, 1.8, 6],                    // board depth fraction, + px, capped at px, per bag back
  stackDrift: [1.2, 0.8],                       // px sideways drift per bag back
  // Each rear bag's own seeded variation, so a stack reads as placed by hand.
  stackJitter: {
    dx: 3,                                      // ± px sideways
    rise: [0.75, 1.25],                         // × the rise step
    rz: 1.5,                                    // ± degrees of twist
    scale: [0.96, 0.99],                        // per bag back
    shade: [0.8, 1.2],                          // × the brightness step
    flip: 0.2,                                  // chance a bag sits out to the other side
  },
  perspective: 700,                             // px, for the seeded yaw
  liftShadow: 'drop-shadow(-9px 14px 12px rgba(40, 26, 12, .40))',
  contact: 'radial-gradient(ellipse at 50% 40%, rgba(22, 12, 4, .62), rgba(22, 12, 4, 0) 70%)',
  clipShadow: '0 1px 1.5px rgba(0, 0, 0, .25)',
  dim: 'brightness(.45) saturate(.3)',          // filtered out
  lift: { y: -10, scale: 1.06, ms: 180 },       // picked
  rerack: { ms: 520, stagger: 9, maxStagger: 22 }, // FLIP on regroup: ~720 ms end to end
};

// How the wall is framed between the Shelf's glass card and its dock (ShelfWall).
export const FRAME = {
  topReach: 0.15,       // the tallest top-board bag's top, above BOARDS[0].back (plate-height fraction)
  cardGap: 8,           // px between the card and that bag top
  topFill: 40,          // px at the very top the plate may fall short by (filled with its own top edge, mirrored)
  dockGap: 50,          // px from the bottom board's lip to the dock (the shelf ticket hangs up to ~41px under the lip)
};

// Stable 0..1 per string (FNV-1a).
export function hash01 (str) {
  let h = 2166136261;
  for (const ch of str) { h ^= ch.charCodeAt(0); h = Math.imul(h, 16777619); }
  return ((h >>> 0) % 10000) / 10000;
}

function lightness (hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return 0.5;
  const n = parseInt(m[1], 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => v / 255);
  return (Math.max(r, g, b) + Math.min(r, g, b)) / 2;
}

/** The blank pouch a roaster's stickered coffees go on: stable per roaster, from its colour. */
export function pouchFor (roasterSlug) {
  const l = lightness(roasterColour(roasterSlug || ''));
  if (l >= 0.68) return 'white';
  if (l <= 0.3) return 'black';
  return 'kraft';
}

const cache = new Map();

/**
 * Everything needed to draw a coffee's front.
 * { kind: 'cut'|'crop'|'sticker', src, aspect, pouch?, sticker?: { src, w, h, x, y, rot } }
 * aspect is the drawn front's w/h. Sticker geometry is in fractions of the front.
 */
export function frontOf (coffee) {
  const slug = typeof coffee === 'string' ? coffee : coffee?.slug;
  if (!slug) return null;
  if (cache.has(slug)) return cache.get(slug);
  const f = fronts[slug];
  let out;
  if (f?.mode === 'sticker') {
    const p = POUCHES[pouchFor(coffee?.roasterSlug)];
    const style = STICKER_STYLE[coffee?.roasterSlug] || {};
    const [lo, hi] = STICKER.aspect;
    const a = style.aspect || Math.min(hi, Math.max(lo, f.aspect || 1));
    let w = STICKER.width;
    let h = (w * p.aspect) / a;                       // label height as a fraction of pouch height
    if (h > STICKER.maxHeight) { w *= STICKER.maxHeight / h; h = STICKER.maxHeight; }
    out = {
      kind: 'sticker',
      src: p.src,
      aspect: p.aspect,
      pouch: pouchFor(coffee?.roasterSlug),
      sticker: {
        src: asset(`/bags/${slug}.webp`),
        small: asset(`/bags/sm/${slug}.webp`),
        w, h,
        x: 0.5 - w / 2 + (hash01(slug + ':sx') - 0.5) * 0.02,
        y: STICKER.centreY - h / 2,
        rot: (hash01(slug + ':rot') - 0.5) * 2 * STICKER.tilt,
        zoom: style.zoom || 1,
        fx: style.fx ?? 0.5,
        fy: style.fy ?? 0.45,
        typed: style.typed ? typedLabel(coffee) : null,
      },
    };
  } else if (f?.cut) {
    out = { kind: 'cut', src: asset(`/bags/cut/${slug}.webp`), small: asset(`/bags/sm/cut/${slug}.webp`), aspect: f.cutAspect || f.aspect || 0.72 };
  } else {
    out = { kind: 'crop', src: asset(`/bags/${slug}.webp`), small: asset(`/bags/sm/${slug}.webp`), aspect: f?.aspect || 0.72 };
  }
  if (typeof coffee === 'object') cache.set(slug, out);
  return out;
}

/** The words on a printed stock label: roaster, coffee, process · brew. */
function typedLabel (c) {
  return {
    roaster: String(c?.roaster || '').replace(/\s+coffee(\s+roasters)?$/i, ''),
    name: shortName(c, 26),
    line: [c?.process?.[0], c?.brew].filter(Boolean).join(' · '),
  };
}

/** Every image URL a front needs (for preloading / decode). */
export function frontUrls (coffee) {
  const f = frontOf(coffee);
  if (!f) return [];
  return f.sticker && !f.sticker.typed ? [f.src, f.sticker.src] : [f.src];
}

// --- what goes on the shelf ---------------------------------------------------

const smallest = (c) => (c.offers || []).reduce((m, o) => (!m || o.grams < m.grams ? o : m), null);

/** A 1 kg blend: it is stocked in the big bag on the bottom board. */
export function isKgBlend (c) {
  return (c.offers || []).some((o) => o.grams === 1000) && (c.kind === 'Blend' || /\bblend\b/i.test(c.name || c.slug));
}

/** The pack size a coffee is shelved in (grams). */
export function shelvedGrams (c) {
  return isKgBlend(c) ? 1000 : (smallest(c)?.grams || 250);
}

/** The price clip under a bag: the shelved size's price. */
// The brand line of a shelf ticket: the roaster without the trade words.
export function brandText (c) {
  return String(c?.roaster || '').replace(/\s+(coffee\s+roasters|roasters|coffee|roastery)$/i, '').trim();
}

export function clipText (c, grams = shelvedGrams(c)) {
  if (!c.inStock) return 'Sold out';
  const o = (c.offers || []).find((x) => x.grams === grams) || smallest(c);
  const p = o?.price ?? c.fromPrice;
  if (p == null) return '';
  const n = Number.isInteger(p) ? `$${p}` : `$${p.toFixed(2)}`;
  return grams >= 1000 ? `${n} · 1kg` : n;
}

/**
 * Store bays → the placement engine's input. Blends and each bay's first two
 * in-stock coffees are the fast movers (best-seller facings); the 1 kg blends
 * go to the bottom board.
 */
export function wallInput (bays) {
  return bays.map((b) => {
    let hits = 0;
    return {
      key: b.key,
      coffees: b.coffees.map((c) => {
        const pop = c.inStock && (c.kind === 'Blend' || hits < 2);
        if (c.inStock && c.kind !== 'Blend' && hits < 2) hits += 1;
        return {
          slug: c.slug,
          grams: shelvedGrams(c) >= 1000 ? 250 : shelvedGrams(c),
          aspect: frontOf(c)?.aspect || 0.72,
          inStock: !!c.inStock,
          kg: isKgBlend(c),
          popular: pop,
        };
      }),
    };
  });
}

/** "[FILTER] ETHIOPIA MENSUR ABAHIKA - WASHED G2" → "Ethiopia Mensur Abahika". */
export function shortName (coffee, max = 30) {
  const n = String(coffee?.name || '').replace(/\[[^\]]*\]/g, ' ').replace(/\s+/g, ' ').trim();
  const brewWord = /^(filter|espresso|omni)( roast)?$/i;
  const segs = n.split(/\s+(?:\||-|–|\/)\s+|:\s+/).map((s) => s.trim()).filter(Boolean);
  let parts = segs.filter((s, i) => !(i > 0 && brewWord.test(s)));
  parts = parts.map((s) => (s.split(' ').length > 2 ? s.replace(/\s+(filter|espresso)( roast)?$/i, '') : s).trim());
  let out = parts[0] || n;
  for (let i = 1; i < parts.length; i++) {
    const next = `${out} ${parts[i]}`;
    const originOnly = coffee?.origins?.some((o) => o.toLowerCase() === out.toLowerCase());
    if (next.length <= max || (originOnly && i === 1)) out = next; else break;
  }
  const letters = out.replace(/[^A-Za-z]/g, '');
  if (letters && letters.replace(/[^A-Z]/g, '').length / letters.length > 0.7) {
    out = out.toLowerCase().replace(/\b([a-z])/g, (m) => m.toUpperCase()).replace(/\b(Aa|Ab|G[12]|Sl\d+)\b/g, (m) => m.toUpperCase());
  }
  return out;
}
